import { create } from 'zustand';

/**
 * memoPanelStore (design-20260724-rightrail-memo) — ⑤ 우측 레일 메모 패널의 **표시 상태**.
 * 목록/본문 데이터는 memoStore 가 소유한다(관심사 분리).
 *
 * 영속(localStorage): 도킹·폭·정렬 선호는 사용자 취향이라 새로고침에도 유지.
 * 열림 여부는 영속하지 않는다 — 새로고침 때 패널이 저절로 떠 있으면 놀란다.
 */

export const MEMO_PANEL_MIN_WIDTH = 300;
export const MEMO_PANEL_MAX_WIDTH = 640;
export const MEMO_PANEL_DEFAULT_WIDTH = 360;

const LS_WIDTH = 'memo.panel.width';
const LS_DOCK = 'memo.panel.dock';
const LS_CONTEXT_FIRST = 'memo.sort.contextFirst';

export type MemoDock = 'overlay' | 'inline';
export type MemoView = 'list' | 'editor';

function clampWidth(w: number): number {
  return Math.min(MEMO_PANEL_MAX_WIDTH, Math.max(MEMO_PANEL_MIN_WIDTH, Math.round(w)));
}

function readWidth(): number {
  try {
    const v = Number(localStorage.getItem(LS_WIDTH));
    return Number.isFinite(v) && v > 0 ? clampWidth(v) : MEMO_PANEL_DEFAULT_WIDTH;
  } catch {
    return MEMO_PANEL_DEFAULT_WIDTH;
  }
}

function readDock(): MemoDock {
  try {
    // 기본은 오버레이(결정 D-4) — 본문을 밀지 않는다.
    return localStorage.getItem(LS_DOCK) === 'inline' ? 'inline' : 'overlay';
  } catch {
    return 'overlay';
  }
}

function readContextFirst(): boolean {
  try {
    // 기본 ON — 명시적으로 끈 사람만 false.
    return localStorage.getItem(LS_CONTEXT_FIRST) !== '0';
  } catch {
    return true;
  }
}

function persist(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage 비활성(프라이빗 모드 등) — 세션 내 동작은 유지 */
  }
}

interface MemoPanelState {
  open: boolean;
  view: MemoView;
  /** 편집 중인 메모 id. null = 새 메모(로컬 draft). */
  activeId: string | null;
  dock: MemoDock;
  width: number;
  /** '이 화면 우선' 정렬(§4.9). 끄면 순수 최근순. */
  contextFirst: boolean;

  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  showList: () => void;
  showEditor: (id: string | null) => void;
  toggleDock: () => void;
  setWidth: (w: number) => void;
  resetWidth: () => void;
  toggleContextFirst: () => void;
}

export const useMemoPanelStore = create<MemoPanelState>((set) => ({
  open: false,
  // 재오픈 시 항상 목록부터(결정 D-2 — 예측 가능성 우선).
  view: 'list',
  activeId: null,
  dock: readDock(),
  width: readWidth(),
  contextFirst: readContextFirst(),

  openPanel: () => set({ open: true, view: 'list', activeId: null }),
  closePanel: () => set({ open: false }),
  togglePanel: () => set((s) => (s.open ? { open: false } : { open: true, view: 'list', activeId: null })),
  showList: () => set({ view: 'list', activeId: null }),
  showEditor: (id) => set({ view: 'editor', activeId: id }),

  toggleDock: () => set((s) => {
    const next: MemoDock = s.dock === 'overlay' ? 'inline' : 'overlay';
    persist(LS_DOCK, next);
    return { dock: next };
  }),

  setWidth: (w) => {
    const next = clampWidth(w);
    persist(LS_WIDTH, String(next));
    set({ width: next });
  },
  resetWidth: () => {
    persist(LS_WIDTH, String(MEMO_PANEL_DEFAULT_WIDTH));
    set({ width: MEMO_PANEL_DEFAULT_WIDTH });
  },

  toggleContextFirst: () => set((s) => {
    const next = !s.contextFirst;
    persist(LS_CONTEXT_FIRST, next ? '1' : '0');
    return { contextFirst: next };
  }),
}));
